import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Typography,
  NotFound,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  FormControl,
  Stack,
  Grid
} from '@esgian/esgianui';
import moment from 'moment/moment';
import { useQuery } from '@tanstack/react-query';
import { getCompanyPortCalls } from '@api';
import { useSegment } from '@hooks';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import CompanyPortTable from '@components/Sections/ShipAnalytics/CompanyPortStatisticsSection/CompanyPortTable';
import CompanyPortChart from '@components/Sections/ShipAnalytics/CompanyPortStatisticsSection/CompanyPortChart';

function CompanyPortStatisticsSection({
  companyType = 'operator',
  companyId = null,
  companyName = ''
}) {
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const { segment, segmentMinDate } = useSegment();

  const years = useMemo(() => {
    let res = [];
    for (let i = moment().year(); i > moment(segmentMinDate).year(); i--) {
      res.push(i);
    }
    return res;
  }, []);

  const portStatsQuery = useQuery({
    queryKey: [
      'portStatsQuery',
      {
        SegmentTypeId: segment.id,
        ...(companyType === 'operator' && {
          OperatorTypeId: operatorType,
          Operators: [companyId]
        }),
        ...(companyType === 'owner' && {
          Owners: [companyId]
        }),
        CommercialCategoryId: commercialCategoryType,
        LastPortCallType: 0,
        StartDate: moment
          .utc(`${selectedYear - 1}`, 'YYYY')
          .startOf('year')
          .format('YYYY-MM-DD HH:mm:ss'),
        EndDate: moment.utc(`${selectedYear}`, 'YYYY').endOf('year').format('YYYY-MM-DD HH:mm:ss')
      }
    ],
    enabled: !!selectedYear && !!companyId,
    placeholderData: null,
    queryFn: ({ queryKey, signal }) => {
      return getCompanyPortCalls(queryKey[1], signal)
        .then((result) => {
          return result;
        })
        .catch((e) => {
          console.log(e);
          return null;
        });
    }
  });

  return (
    <>
      {(portStatsQuery.isFetching || (portStatsQuery.isFetched && portStatsQuery?.data)) && (
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid item xs={12}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">Top Ports and Share of Port Calls</Typography>
                    <FormControl sx={{ minWidth: 120 }} size="small">
                      <InputLabel id="top-company-year-label">Year</InputLabel>
                      <Select
                        id="top-company-year-select"
                        value={selectedYear}
                        label="Year"
                        onChange={({ target }) => setSelectedYear(target.value)}>
                        {years?.map((year) => (
                          <MenuItem key={`select-${year}`} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <CompanyPortChart
                    statsData={portStatsQuery.data}
                    loading={portStatsQuery.isFetching}
                    fileName={`operator-stats-${companyName?.toLowerCase().split(' ').join('-')}`}
                    showYTD={selectedYear === moment().year()}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <CompanyPortTable
              statsData={portStatsQuery.data}
              loading={portStatsQuery.isFetching}
              fileName={`${companyType === 'operator' ? 'operator' : 'owner'}-stats-${companyName
                ?.toLowerCase()
                .split(' ')
                .join('-')}`}
              companyType={companyType}
              companyId={companyId}
              showYTD={selectedYear === moment().year()}
            />
          </Grid>
        </Grid>
      )}

      {portStatsQuery.isFetched && !portStatsQuery.data && (
        <Grid item xs={12} sx={{ p: 2 }}>
          <NotFound show header="No Matching Result" text="Please change your search parameters" />
        </Grid>
      )}
    </>
  );
}

CompanyPortStatisticsSection.propTypes = {
  companyType: PropTypes.string,
  companyId: PropTypes.number,
  companyName: PropTypes.string
};

export default CompanyPortStatisticsSection;
